import { Table, TableBody, TableHead, TableRow } from 'components/Table';
import styles from './styles/GameQuarterByQuarter.module.scss';
import TeamBlock from 'components/TeamBlock';
import { useGameContainer } from '../index';
import { BASEBALL, SOFTBALL } from 'constants/Sports';
import cn from 'classnames';
import useScreenSize from 'hooks/useScreenSize';
import { GamesShowQuery } from 'queries';
import { SectionHeaderSize } from '@/components/SectionHeader';
import ContentSection, { ContentSectionHeader } from '@/components/ContentSection';
import ApplicationShareButton from 'containers/ApplicationContainer/components/ApplicationShareButton';

type LinescorePeriodType = NonNullable<GamesShowQuery['game']['linescore']>['periods'];

// TODO this should happen in graphQl
const linescoreToInnings = (linescore: LinescorePeriodType) => {
  // We only do this processing if there are more or equal to fourteen entries
  // in the linescore and there are an even number of elements (for top and bottom of
  // innings, without the 'T' linescore item). Otherwise we assume that the game's linescores
  // each correspond to a complete inning.
  if (linescore && linescore.length >= 14 && (linescore.length - 1) % 2 == 0) {
    let inningScores = linescore
      .reduce((resultArray: LinescorePeriodType[], item, index) => {
        let chunkIndex = Math.floor(index / 2); // We want chunks of inning top and bottom

        if (!resultArray[chunkIndex]) {
          resultArray[chunkIndex] = [];
        }

        resultArray[chunkIndex].push(item);

        return resultArray;
      }, [])
      .map(inningScore => {
        if (inningScore.length == 1) {
          return inningScore[0];
        }

        let inning = { id: inningScore[0].id, label: inningScore[0].label, home: 0, away: 0 };

        inning.away = (inningScore[0].away || 0) + (inningScore[1].away || 0);
        inning.home = (inningScore[0].home || 0) + (inningScore[1].home || 0);

        return inning;
      });

    return inningScores;
  } else {
    return linescore;
  }
};

export default function GameQuarterByQuarter() {
  const [{ game }] = useGameContainer();
  const { isSmallScreen } = useScreenSize();

  const {
    linescore,
    gameTeams,
    genderSport: { sportId }
  } = game;

  if (!linescore) return null;

  const periods = [BASEBALL, SOFTBALL].includes(sportId) ? linescoreToInnings(linescore.periods) : linescore.periods;

  return (
    <ContentSection>
      <ContentSectionHeader size={SectionHeaderSize.LARGE} right={<ApplicationShareButton />}>
        Line Score
      </ContentSectionHeader>
      <Table className={styles.QuarterByQuarterTable} reducedPadding v2>
        <TableHead headers={['team', ...periods.map(period => period.label)]} />
        <TableBody>
          <TableRow>
            <td>
              <TeamBlock
                className={styles.QuarterByQuarterTeamName}
                image={gameTeams[0].team.image}
                name={(isSmallScreen ? gameTeams[0].team.abbrev : gameTeams[0].team.name) || 'Away'}
                link={gameTeams[0].team.webPath}
              />
            </td>
            {periods.map((period, i) => (
              <td className={cn(styles.QuarterByQuarterColumn, { [styles.Total]: linescore && i === periods.length - 1 })} key={i}>
                {period.away !== null ? period.away : '-'}
              </td>
            ))}
          </TableRow>
          <TableRow>
            <td>
              <TeamBlock
                className={styles.QuarterByQuarterTeamName}
                image={gameTeams[1].team.image}
                name={(isSmallScreen ? gameTeams[1].team.abbrev : gameTeams[1].team.name) || 'Home'}
                link={gameTeams[1].team.webPath}
              />
            </td>
            {periods.map((period, i) => (
              <td className={cn(styles.QuarterByQuarterColumn, { [styles.Total]: linescore && i === periods.length - 1 })} key={i}>
                {period.home !== null ? period.home : '-'}
              </td>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </ContentSection>
  );
}
